import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClientCard } from './ClientCard';
import { Client, ClientLifecycleStage, LIFECYCLE_STAGES, countsTowardMRR } from '@/types/crm';
import { CheckCircle, MessageCircle, PauseCircle, Rocket } from 'lucide-react';

interface ClientPipelineColumnProps {
  stage: ClientLifecycleStage;
  clients: Client[];
  onClientClick?: (client: Client) => void;
  onQuickMove?: (client: Client) => void;
  savingClientId?: string | null;
  isBulkMode?: boolean;
  selectedClientIds?: Set<string>;
  onToggleSelect?: (clientId: string) => void;
}

export function ClientPipelineColumn({ stage, clients, onClientClick, onQuickMove, savingClientId, isBulkMode, selectedClientIds, onToggleSelect }: ClientPipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
  });

  const stageConfig = LIFECYCLE_STAGES[stage];
  const clientIds = clients.map(c => c.id);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate total — one-off deals are single payments, not monthly, so they are
  // held out of the column subtotal and counted separately.
  const totalValue = clients.reduce((sum, c) => {
    if (!countsTowardMRR(c.revenue_type)) return sum;
    const value = c.confirmed_monthly_revenue || c.estimated_monthly_value || 0;
    return sum + value;
  }, 0);
  const oneOffCount = clients.filter(c => !countsTowardMRR(c.revenue_type)).length;

  // Determine badge variant based on stage
  const getBadgeVariant = () => {
    switch (stage) {
      case 'active':
        return 'default';
      case 'committed':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      className={`
        flex flex-col min-h-[280px]
        flex-shrink-0 w-[85vw] min-w-[200px] snap-center max-h-[70dvh]
        sm:w-auto sm:min-w-0 sm:flex-shrink sm:snap-align-none sm:max-h-none
        transition-all duration-200
        ${isOver ? 'ring-2 ring-primary ring-offset-2 bg-accent/30' : ''}
      `}
    >
      <CardHeader className="pb-2 sticky top-0 z-10 bg-card">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {stageConfig.label}
            <Badge variant={getBadgeVariant()} className="text-xs">
              {clients.length}
            </Badge>
          </CardTitle>
        </div>
        <div className="text-xs text-muted-foreground flex items-center justify-between">
          <span>Total:</span>
          <span className="font-medium font-serif">{totalValue > 0 ? formatCurrency(totalValue) : '-'}</span>
        </div>
        {oneOffCount > 0 && (
          <div className="text-[10px] text-muted-foreground">
            + {oneOffCount} one-off deal{oneOffCount !== 1 ? 's' : ''}
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
        {/* Drop zone hints */}
        {isOver && stage === 'interest' && (
          <div className="bg-info/10 border border-info/30 rounded-md p-2 text-xs text-info flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            <span>Drop to mark intro as made</span>
          </div>
        )}
        {isOver && stage === 'committed' && (
          <div className="bg-accent/10 border border-accent/30 rounded-md p-2 text-xs text-foreground flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            <span>Drop to mark as signed (contract signed)</span>
          </div>
        )}
        {isOver && stage === 'paused' && (
          <div className="bg-muted border border-border rounded-md p-2 text-xs text-muted-foreground flex items-center gap-2">
            <PauseCircle className="h-4 w-4" />
            <span>Drop to pause this client</span>
          </div>
        )}
        {isOver && stage === 'active' && (
          <div className="bg-primary/10 border border-primary/30 rounded-md p-2 text-xs text-primary flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            <span>Drop to activate client</span>
          </div>
        )}

        <SortableContext items={clientIds} strategy={verticalListSortingStrategy}>
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={() => isBulkMode && onToggleSelect
                ? onToggleSelect(client.id)
                : onClientClick?.(client)
              }
              onQuickMove={onQuickMove ? () => onQuickMove(client) : undefined}
              isSaving={savingClientId === client.id}
              isBulkMode={isBulkMode}
              isSelected={selectedClientIds?.has(client.id)}
            />
          ))}
        </SortableContext>

        {clients.length === 0 && !isOver && (
          <div className="text-xs text-muted-foreground text-center py-8">
            No clients
          </div>
        )}
      </CardContent>
    </Card>
  );
}
